import Dialog, { DialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import EmptyState from '@liquidweb-dev/undercurrent/EmptyState';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import List from '@liquidweb-dev/undercurrent/List';
import ListItem from '@liquidweb-dev/undercurrent/ListItem';
import ListItemButton from '@liquidweb-dev/undercurrent/ListItemButton';
import ListItemText from '@liquidweb-dev/undercurrent/ListItemText';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import Typography from '@liquidweb-dev/undercurrent/Typography';

import StarIcon from '@liquidweb-dev/undercurrent/icons/lucide/Star';
import StarOffIcon from '@liquidweb-dev/undercurrent/icons/lucide/StarOff';

import useStateDataListForKey from 'modules/queries/account/user/stateData/useListForKey';
import { useRemoveFavorite } from 'modules/queries/account/user/stateData/useRemove';
import { Link as RouterLink } from 'react-router-dom';

export interface DialogFavoritesProps extends Omit<DialogProps, 'onClose'> {
	open: boolean;
	onClose: () => void;
}

const DialogFavorites = ({ open, onClose, ...rest }: DialogFavoritesProps) => {
	const { mutate: removeFavorite } = useRemoveFavorite();
	const { data, isLoading } =
		useStateDataListForKey({ key: 'favorites' }, { enabled: open }) || {};
	const favoritesData = Array.isArray(data?.data) ? data.data : [];

	const handleClick = (uuid: string) => removeFavorite({ uuid });

	if (isLoading) {
		return (
			<Dialog maxWidth="md" fullWidth open={open}>
				<DialogTitle
					onClose={onClose}
					sx={{ color: ({ vars }) => vars.palette.uc.text.main }}
				>
					Your Favorites
				</DialogTitle>
				<DialogContent>
					<Skeleton />
					<Skeleton width="75%" />
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<Dialog maxWidth="md" fullWidth open={open} onClose={onClose} {...rest}>
			<DialogTitle
				onClose={onClose}
				sx={{
					color: ({ vars }) => vars.palette.uc.text.main,
				}}
			>
				Your Favorites
			</DialogTitle>
			<DialogContent sx={{ pb: 300 }}>
				{favoritesData.length > 0 ? (
					<List
						sx={(theme) => ({
							minHeight: 0,
							maxHeight: 'none',
							borderColor: theme.vars.palette.uc.border.weak,
						})}
					>
						{favoritesData.map((item) => (
							<ListItem
								key={item.uuid}
								secondaryAction={
									<Tooltip title="Remove">
										<IconButton
											variant="ghost"
											color="highlight"
											onClick={() => handleClick(item.uuid)}
										>
											<StarOffIcon />
										</IconButton>
									</Tooltip>
								}
							>
								<ListItemButton
									component={RouterLink}
									to={item.path as string}
									onClick={onClose}
								>
									<ListItemText
										primary={item.displayName}
										secondary={item.path}
										sx={{
											flexDirection: 'column',
											color: ({ vars }) => vars.palette.uc.text.main,
											alignItems: 'self-start',
											gap: 50,
										}}
										slotProps={{
											primary: {
												variant: 'label-lg',
												sx: { fontWeight: 'medium' },
											},
											secondary: {
												variant: 'body-sm',
												sx: { fontFamily: 'monospace' },
											},
										}}
									/>
								</ListItemButton>
							</ListItem>
						))}
					</List>
				) : (
					<List
						sx={{
							display: 'flex',
							flex: 1,
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							width: '100%',
							maxHeight: 'none',
							borderColor: ({ vars }) => vars.palette.uc.border.weak,
						}}
					>
						<EmptyState
							icon={
								<StarIcon
									sx={(theme) => ({
										height: theme.size.md,
										width: theme.size.md,
										color: ({ vars }) => vars.palette.uc.icon.main,
									})}
								/>
							}
						>
							<Typography
								variant="body-md"
								sx={{ color: ({ vars }) => vars.palette.uc.text.main }}
							>
								You don&apos;t have any favorites
							</Typography>
						</EmptyState>
					</List>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default DialogFavorites;
